import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Bell, DollarSign, Calendar, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeeReminderSettingsPage = () => {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState({
    enabled: false,
    days_before_due: 7,
    send_on_due_date: true,
    days_after_due: [1, 3, 7],
    channels: ['in_app', 'email']
  });
  const [stats, setStats] = useState({
    overdueFees: 0,
    upcomingDues: 0,
    totalPending: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fee-reminder-settings`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const feesResponse = await axios.get(`${API_URL}/api/fees`, {
        headers: getAuthHeader()
      });
      
      const fees = feesResponse.data;
      const today = new Date();
      
      const overdue = fees.filter(f => {
        const dueDate = new Date(f.due_date);
        return dueDate < today && f.status !== 'paid';
      }).length;
      
      const upcoming = fees.filter(f => {
        const dueDate = new Date(f.due_date);
        const diff = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));
        return diff >= 0 && diff <= 7 && f.status !== 'paid';
      }).length;
      
      const pending = fees.filter(f => f.status !== 'paid')
        .reduce((sum, f) => sum + (f.amount - f.paid_amount), 0);
      
      setStats({
        overdueFees: overdue,
        upcomingDues: upcoming,
        totalPending: pending
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/fee-reminder-settings`, settings, {
        headers: getAuthHeader()
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRemindersNow = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/send-fee-reminders`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Reminders sent! ${response.data.sent_count} notifications queued.`);
      fetchStats();
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setLoading(false);
    }
  };

  const toggleChannel = (channel) => {
    setSettings(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  return (
    <DashboardLayout title="Fee Reminder Settings">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue Fees</CardTitle>
              <DollarSign className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.overdueFees}</div>
              <p className="text-xs text-muted-foreground">Students with overdue payments</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Upcoming Dues</CardTitle>
              <Calendar className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{stats.upcomingDues}</div>
              <p className="text-xs text-muted-foreground">Due within 7 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Pending</CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">₹{stats.totalPending.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Outstanding amount</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Action */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button 
                onClick={handleSendRemindersNow} 
                disabled={loading}
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Reminders Now
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              This will send reminders to all students with overdue or upcoming dues based on your settings below.
            </p>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Automated Reminder Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Enable Automated Reminders</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically send fee reminders based on schedule
                </p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            <div className="border-t pt-6 space-y-4">
              <div className="space-y-2">
                <Label>Days Before Due Date</Label>
                <Input
                  type="number"
                  value={settings.days_before_due}
                  onChange={(e) => setSettings({ ...settings, days_before_due: parseInt(e.target.value) })}
                  min="1"
                  max="30"
                />
                <p className="text-xs text-muted-foreground">
                  Send reminder {settings.days_before_due} days before the due date
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Send on Due Date</Label>
                  <p className="text-sm text-muted-foreground">
                    Send a reminder on the actual due date
                  </p>
                </div>
                <Switch
                  checked={settings.send_on_due_date}
                  onCheckedChange={(checked) => setSettings({ ...settings, send_on_due_date: checked })}
                />
              </div>

              <div className="space-y-2">
                <Label>Overdue Reminders (Days After Due)</Label>
                <div className="flex gap-2">
                  {[1, 3, 7, 14, 30].map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const days = settings.days_after_due.includes(day)
                          ? settings.days_after_due.filter(d => d !== day)
                          : [...settings.days_after_due, day].sort((a, b) => a - b);
                        setSettings({ ...settings, days_after_due: days });
                      }}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        settings.days_after_due.includes(day)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {day}d
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Send reminders {settings.days_after_due.join(', ')} days after due date
                </p>
              </div>

              <div className="space-y-2">
                <Label>Notification Channels</Label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleChannel('in_app')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.channels.includes('in_app')
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Bell className="h-4 w-4" />
                    In-App
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleChannel('email')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.channels.includes('email')
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleChannel('sms')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.channels.includes('sms')
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    SMS
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleChannel('whatsapp')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      settings.channels.includes('whatsapp')
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    WhatsApp
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Note: Email, SMS, WhatsApp require integration setup
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveSettings} disabled={loading}>
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FeeReminderSettingsPage;
